// @ts-nocheck
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Trophy, Eye, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const emptyForm = {
  name: "",
  description: "",
  is_public: true,
  is_active: true,
  // Point system
  config_name: "Default Scoring",
  win_points: 3,
  loss_points: 1,
  margin_bonus_enabled: false,
  margin_cap: 20,
  margin_bonus_per_point: 0.5,
  shoe_team_bonus: 0,
  home_win_bonus_points: 0,
  away_win_bonus_points: 0,
};

export default function CircuitsManagement() {
  const { scopeOrgId, createOrgId, isSuperAdmin, ready } = useAdminOrgScope();
  const orgId = createOrgId;
  const qc = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const { data: circuits = [], isLoading } = useQuery({
    queryKey: ["circuits-admin", scopeOrgId, isSuperAdmin],
    enabled: ready,
    queryFn: async () => {
      let query = (supabase as any).from("circuits").select("*").order("name");
      if (scopeOrgId) query = query.eq("organization_id", scopeOrgId);
      const { data } = await query;
      return data || [];
    },
  });

  // Resolve the org's subdomain so the "View public" link points at the
  // correct org-scoped public URL (slugs are only unique per-org).
  const { data: orgSubdomain } = useQuery({
    queryKey: ["circuit-org-subdomain", scopeOrgId],
    enabled: ready && !!scopeOrgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("organizations")
        .select("subdomain")
        .eq("id", scopeOrgId)
        .maybeSingle();
      return data?.subdomain ?? null;
    },
  });

  const publicCircuitUrl = (slug: string) => {
    // Carry org context so the per-org slug resolves correctly. On a live org
    // subdomain we use the subdomain; everywhere else (preview/apex) we fall
    // back to a relative link with an ?org= param the public page reads.
    const host = window.location.hostname;
    const onLiveDomain =
      host.endsWith("recruitlookevents.com") && !host.startsWith("www.");
    if (orgSubdomain && onLiveDomain) {
      return `https://${orgSubdomain}.recruitlookevents.com/circuits/${slug}`;
    }
    return `/circuits/${slug}${scopeOrgId ? `?org=${scopeOrgId}` : ""}`;
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = async (c: any) => {
    setEditing(c);
    let config: any = null;
    if (c.scoring_config_id) {
      const { data } = await (supabase as any)
        .from("circuit_scoring_configs")
        .select("*")
        .eq("id", c.scoring_config_id)
        .maybeSingle();
      config = data;
    }
    setForm({
      name: c.name || "",
      description: c.description || "",
      is_public: c.is_public,
      is_active: c.is_active,
      config_name: config?.name || "Default Scoring",
      win_points: config?.win_base_points ?? 3,
      loss_points: config?.loss_base_points ?? 1,
      margin_bonus_enabled: config?.margin_bonus_enabled ?? false,
      margin_cap: config?.margin_cap ?? 20,
      margin_bonus_per_point: config?.margin_bonus_per_point ?? 0.5,
      shoe_team_bonus: config?.shoe_team_bonus ?? 0,
      home_win_bonus_points: config?.home_win_bonus_points ?? 0,
      away_win_bonus_points: config?.away_win_bonus_points ?? 0,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name required");
      return;
    }
    setSaving(true);
    try {
      // 1. Upsert the scoring config
      const configPayload: any = {
        organization_id: orgId,
        name: form.config_name?.trim() || "Default Scoring",
        win_base_points: Number(form.win_points) || 0,
        loss_base_points: Number(form.loss_points) || 0,
        margin_bonus_enabled: form.margin_bonus_enabled,
        margin_cap: Math.round(Number(form.margin_cap) || 0),
        margin_bonus_per_point: Number(form.margin_bonus_per_point) || 0,
        shoe_team_bonus: Number(form.shoe_team_bonus) || 0,
        home_win_bonus_points: Number(form.home_win_bonus_points) || 0,
        away_win_bonus_points: Number(form.away_win_bonus_points) || 0,
      };

      let configId = editing?.scoring_config_id || null;
      if (configId) {
        const { error: cErr } = await (supabase as any)
          .from("circuit_scoring_configs")
          .update(configPayload)
          .eq("id", configId);
        if (cErr) throw cErr;
      } else {
        const { data: cData, error: cErr } = await (supabase as any)
          .from("circuit_scoring_configs")
          .insert(configPayload)
          .select("id")
          .single();
        if (cErr) throw cErr;
        configId = cData.id;
      }

      // 2. Upsert the circuit
      const payload: any = {
        name: form.name.trim(),
        description: form.description,
        is_public: form.is_public,
        is_active: form.is_active,
        organization_id: orgId,
        slug: slugify(form.name),
        scoring_config_id: configId,
      };
      let error;
      if (editing) {
        ({ error } = await (supabase as any)
          .from("circuits")
          .update(payload)
          .eq("id", editing.id));
      } else {
        ({ error } = await (supabase as any).from("circuits").insert(payload));
      }
      if (error) throw error;

      toast.success(editing ? "Circuit updated" : "Circuit created");
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ["circuits-admin", scopeOrgId, isSuperAdmin] });
    } catch (e: any) {
      toast.error(e.message || "Failed to save circuit");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: any) => {
    if (!confirm(`Delete circuit "${c.name}"? This will also remove its event links and standings.`))
      return;
    const { error } = await (supabase as any).from("circuits").delete().eq("id", c.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Circuit deleted");
    qc.invalidateQueries({ queryKey: ["circuits-admin", scopeOrgId, isSuperAdmin] });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="w-6 h-6 text-primary" /> Circuits
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage circuits and their event links, scoring configs, and standings.
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4 mr-2" /> New Circuit
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : circuits.length === 0 ? (
        <div className="border border-dashed rounded-lg p-12 text-center">
          <Trophy className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground mb-4">
            No circuits yet. Create your first circuit to start tracking standings.
          </p>
          <Button onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" /> Create Circuit
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Public</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {circuits.map((c: any) => (
                <tr key={c.id} className="border-t hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/admin-dashboard/circuits/${c.id}`} className="hover:underline">
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        c.is_public
                          ? "text-green-600 text-xs font-medium"
                          : "text-muted-foreground text-xs"
                      }
                    >
                      {c.is_public ? "Public" : "Private"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/admin-dashboard/circuits/${c.id}`}>
                          <BarChart3 className="w-4 h-4 mr-1.5" />
                          Standings
                        </Link>
                      </Button>
                      {c.is_public && (
                        <Button asChild size="sm" variant="outline">
                          <a
                            href={publicCircuitUrl(c.slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Eye className="w-4 h-4 mr-1.5" />
                            View public
                          </a>
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => remove(c)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Circuit" : "New Circuit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. 17U Boys Spring Circuit"
              />
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.is_public}
                  onCheckedChange={(v) => setForm({ ...form, is_public: v })}
                />
                Public (show on /circuits)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
                Active
              </label>
            </div>

            {/* Point System */}
            <div className="border-t pt-4 space-y-3">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <Trophy className="w-4 h-4 text-primary" /> Point System
              </h4>
              <div>
                <Label>Config name</Label>
                <Input
                  value={form.config_name}
                  onChange={(e) => setForm({ ...form, config_name: e.target.value })}
                  placeholder="Default Scoring"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Win points</Label>
                  <Input
                    type="number"
                    value={form.win_points}
                    onChange={(e) => setForm({ ...form, win_points: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Loss points</Label>
                  <Input
                    type="number"
                    value={form.loss_points}
                    onChange={(e) => setForm({ ...form, loss_points: e.target.value })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.margin_bonus_enabled}
                  onCheckedChange={(v) => setForm({ ...form, margin_bonus_enabled: v })}
                />
                Margin bonus enabled
              </label>
              {form.margin_bonus_enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Margin cap</Label>
                    <Input
                      type="number"
                      value={form.margin_cap}
                      onChange={(e) => setForm({ ...form, margin_cap: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Bonus per point</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.margin_bonus_per_point}
                      onChange={(e) =>
                        setForm({ ...form, margin_bonus_per_point: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}
              <div>
                <Label>Shoe-team bonus</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.shoe_team_bonus}
                  onChange={(e) => setForm({ ...form, shoe_team_bonus: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Home-win bonus</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.home_win_bonus_points}
                    onChange={(e) => setForm({ ...form, home_win_bonus_points: e.target.value })}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Extra points added when the HOME team wins (0 = no effect).</p>
                </div>
                <div>
                  <Label>Away-win bonus</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.away_win_bonus_points}
                    onChange={(e) => setForm({ ...form, away_win_bonus_points: e.target.value })}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Extra points added when the AWAY team wins (0 = no effect).</p>
                </div>
              </div>
            </div>


            {editing && (
              <div className="border-t pt-4">
                <Link
                  to={`/admin-dashboard/circuits/${editing.id}`}
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <BarChart3 className="w-4 h-4" /> View Standings
                </Link>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
